import PageHeader from 'components/general/pageHeader';
import SortFilter from 'components/general/sortFilter';
import { ArrayHelpers, Loading, SystemText, Table, DebugInfo, Box } from 'e4shared';
import React from 'react';
import { getPermissions } from 'helpers/permissions';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { UserMenu } from 'components/user/menu';
import { loadProfile } from 'actions/profile';
import { apiGet } from 'services';
import { e4Format } from 'helpers/dates';
import posthog from 'posthog-js';
import './learning.css';
import NoResults from 'components/general/noResults';
import { translateCourse } from 'helpers/translations';
import { skillColour } from 'helpers/metrics';
import Skills from '../learnTracker/components/skills';
import { setPrismMenu } from 'actions/ui';

export const UserLearning = ({ match, history }) => {
    const dispatch = useDispatch();
    const [filter, setFilter] = React.useState('all');
    const [data, setData] = React.useState([]);
    const [dataProcessed, setDataProcessed] = React.useState([]);
    const [sortBy, setSortBy] = React.useState('status');
    const [ascending, setAscending] = React.useState(false);
    const [page, setPage] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const [showResults, setShowResults] = React.useState(false);
    const { user, coaching, downline } = useSelector(state => state.user);
    const profile = useSelector(state => state.profile.profile);
    const pageSize = 10;

    DebugInfo('component', 'UserLearning');

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));

            if (profile.id === user.id) {
                dispatch(setPrismMenu('Learning'));
            } else {
                dispatch(setPrismMenu('MyTeam'));
            }

            apiGet(`user-learning/${profile.id}`, user.guid, response => {
                setData(
                    response.map(x => {
                        return translateCourse(user.language_Code, x);
                    })
                );

                setLoading(false);
            });
        }
    }, [profile?.id, user?.id]);

    React.useEffect(() => {
        const { id } = match.params;
        if (id) {
            dispatch(loadProfile(id));
        }
    }, [match.params.id]);

    React.useEffect(() => {
        const temp = [...data];
        const up = ascending ? 1 : -1;

        const sortValue = (o, s) => {
            return s === 'status'
                ? `${o.status === 'in_progress' || o.status === 'course_started' ? 1 : o.status}-${o.started_At}`
                : o[s] || '';
        };

        const filtered = temp
            .filter(x => filter === 'all' || x.status === filter)
            .map(x => ({ ...x, sort: sortValue(x, sortBy) }))
            .sort((a, b) => (a.sort > b.sort ? up : up * -1));

        setDataProcessed(ArrayHelpers.getPage(filtered, page, pageSize));
    }, [data, filter, sortBy, ascending, page]);

    if (user && !match.params.id) {
        history.replace(match.path.replace(':id?', user.id));
    }

    React.useEffect(() => {
        setSortBy(filter === 'all' ? 'status' : 'started_At');
        setAscending(true);
        setPage(1);
    }, [filter]);

    React.useEffect(() => {
        posthog.capture('viewed-learning');
    }, []);

    const triggerSort = React.useCallback(x => {
        if (sortBy === x) {
            setAscending(!ascending);
            setPage(1);
        } else {
            setSortBy(x);
            setAscending(false);
            setPage(1);
        }
    });

    const triggerPage = React.useCallback(x => {
        setPage(x);
    });

    const mainButtons = React.useCallback(() => {
        return <UserMenu profile={profile} user={user} hide={'learning'} history={history} />;
    });

    const handleSetFilter = React.useCallback(e => setFilter(e.target.value));

    const btn = (thisFilter, label) => {
        const btnClassName = x => (filter === x ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-default');

        return (
            <button onClick={handleSetFilter} value={thisFilter} className={btnClassName(thisFilter)}>
                {label}
            </button>
        );
    };

    const sortButtons = React.useCallback(() => (
        <div className="btn-row">
            <div className="btn-group mr10" data-testid="usertask-filterswrapper-testid">
                {btn('all', SystemText('User/Learning/Buttons/Filters/All'))}
                {btn('in_progress', SystemText('General/Accelerate/InProgress/Label'))}
                {btn('completed', SystemText('General/Accelerate/Completed/Label'))}
            </div>
        </div>
    ));

    const columns = [
        { label: SystemText('User/Learning/Table/Title'), dataProperty: 'name' },
        {
            label: SystemText('User/Learning/Table/Status'),
            dataProperty: 'status',
            format: d =>
                d === 'completed' || d === 'course_completed' ? (
                    <label className="label label-success">{SystemText('General/Accelerate/Completed/Label')}</label>
                ) : d === 'in_progress' || d === 'course_started' ? (
                    <label className="label label-warning">{SystemText('General/Accelerate/InProgress/Label')}</label>
                ) : (
                    <label className="label label-danger">{SystemText('General/Accelerate/NotStarted/Label')}</label>
                ),
        },
        {
            label: SystemText('User/Learning/Table/Progress'),
            dataProperty: 'score_Percent',
            format: x => <label className="label label-light-grey">{parseInt(x, 10)}%</label>,
        },
        {
            label: SystemText('User/Learning/Table/Started'),
            dataProperty: 'started_At',
            format: x => (x ? e4Format(x) : '-'),
        },
        {
            label: SystemText('User/Learning/Table/LastActivity'),
            dataProperty: 'last_Activity_At',
            format: x => (x ? e4Format(x) : '-'),
        },
        {
            label: SystemText('User/Learning/Table/Completed'),
            dataProperty: 'completed_At',
            format: x => (x ? e4Format(x) : '-'),
        },
        {
            label: SystemText('User/Learning/Table/Results'),
            dataProperty: 'skills_Score',
            format: (x, d) =>
                !x && x !== 0 ? (
                    <span>-</span>
                ) : (
                    <button className={`btn btn-xs btn-light-${skillColour(x)}`} onClick={() => setShowResults(d)}>
                        {x}%
                    </button>
                ),
        },
    ];

    const tableOptions = {
        label: 'Learning',
        sortBy,
        ascending,
        page,
        pageSize: 10,
        recordCount: data.filter(x => filter === 'all' || x.status === filter).length,
        hideOptionsHeader: true,
        //thClass: 'bold',
        extraOptions: [
            {
                btnClass: 'btn-primary btn-fixed-75',
                label: d =>
                    d.status === 'completed' || d.status === 'course_completed'
                        ? SystemText('General/Accelerate/Completed/Button')
                        : d.status === 'in_progress'
                        ? SystemText('General/Accelerate/InProgress/Button')
                        : SystemText('General/Accelerate/NotStarted/Button'),
                onClick: d => {
                    posthog.capture('launched-learning', { course: d.title, course_status: d.status, course_id: d.id });
                    window.location.href = d.url;
                },
            },
        ],
    };

    return (
        <div>
            <PageHeader title={SystemText('User/Learning/Title')} extra={mainButtons} />
            <section className="content mtm10" data-testid="learning-content" id="learning-content">
                <SortFilter hideSortText sortButtons={sortButtons} />
                {loading || !permissions.objectives.canView ? (
                    <span data-testid="user-learning-loading">
                        <Loading />
                    </span>
                ) : (
                    <Box>
                        {dataProcessed.length ? (
                            <Table
                                data={dataProcessed}
                                columns={columns}
                                options={tableOptions}
                                sort={triggerSort}
                                goToPage={triggerPage}
                            />
                        ) : (
                            <NoResults
                                title={SystemText('User/Learning/NoResults/Title')}
                                message={SystemText('User/Learning/NoResults/Message')}
                            />
                        )}
                    </Box>
                )}
            </section>
            <Skills show={showResults} close={() => setShowResults(false)} />
        </div>
    );
};

export default withRouter(UserLearning);
